import { useTranslation } from 'react-i18next';
import { Icon, Typography, Button } from '@/components';
import { customRound, europeanFormatNumber } from '@/helpers';
import { useCountries, useTechnologies } from '@/hooks';
import { extractCountryCodes, extractTechCodes } from '@/features/projects/helpers';
import { TechnologyType } from '@/features/projects/types';

export type PortfolioSummaryProps = {
  readonly normalizedProject: any;
  readonly onEdit?: () => void;
  readonly isEditable: boolean;
};

export function PortfolioSummaryBox({ normalizedProject, isEditable, onEdit }: PortfolioSummaryProps) {
  const { t } = useTranslation(['opportunities']);
  const { map: mapTechnology } = useTechnologies();
  const { kv } = useCountries();

  const topcoTech = normalizedProject.technologies.find((tech: any) => tech.type === 'topco');
  if (!topcoTech) return null;
  const portfolioSummary = normalizedProject.portfolioSummary?.[0];
  const roundedCapacity = customRound(portfolioSummary?.total_mw);
  const technologies = extractTechCodes(portfolioSummary?.technologies)
  .map((code) => mapTechnology(code as TechnologyType))
  .join(', ');
  const markets = extractCountryCodes(portfolioSummary?.markets)
    .map((code) => kv[code])
    .join(', ');

  const fields = [
    {
      label: t('Technologies'),
      value: technologies,
    },
    {
      label: t('Markets'),
      value: markets,
    },
    {
      label: t('Total MW'),
      value: portfolioSummary?.total_mw ? `${europeanFormatNumber(roundedCapacity)} MW` : null,
    },
    {
      label: t('Details'),
      value: portfolioSummary?.details,
    },
  ];

  const visibleFields = isEditable
    ? fields
    : fields.filter(({ value }) => value !== null && value !== undefined && value !== '');

  return (
    <div className="rounded-lg border border-neutral-50 bg-white p-6 shadow-xs">
      <div className="mb-6 flex items-center">
        <div className="flex grow items-center">
          <div className="mr-2 flex h-10 w-10 items-center justify-center rounded-full bg-neutral-50">
            <Icon name="chart-pie" className="h-6 w-6" />
          </div>
          <Typography as="h3" weight="semibold" size="title">
            {t('Portfolio Summary')}
          </Typography>
        </div>
        {isEditable && (
          <Button size="sm" color="neutral" icon="edit" onClick={onEdit} className="ml-auto">
            {t('Edit')}
          </Button>
        )}
      </div>

      <div className="grid grid-cols-1">
        {visibleFields.map(({ label, value }) => (
          <Typography key={label} as="p" size="body-2" weight="semibold" relaxed className="mb-1">
            {label}:{' '}
            <span className="ml-3 text-base font-normal text-neutral-900">{value ?? (isEditable ? '—' : null)}</span>
          </Typography>
        ))}
      </div>
    </div>
  );
}
